import type { PartnerCaptureManifest } from './api/types';

/** The app's own icon, present at every build with no partner env set
 *  (`public/icons/icon-512.svg`), so a build with no brand configured at
 *  all still renders a real tab icon rather than the browser's blank
 *  default. */
export const DEFAULT_LOGO_MARK_URL = '/icons/icon-512.svg';

function buildEnv(name: string): string | undefined {
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  return env[name] || undefined;
}

/**
 * The partner build's favicon and logo, mirroring `classification-ui`'s
 * convention (`src/lib/brand.js`). `deploy-capture-pwa.yml` copies both
 * from the partner whose `captureApi.clientUrl` is this environment's
 * capture domain, and sets these two variables to their paths.
 */
function buildTimeLogoMarkUrl(): string | undefined {
  return buildEnv('VITE_LOGO_MARK_URL');
}

function buildTimeLogoUrl(): string | undefined {
  return buildEnv('VITE_LOGO_URL');
}

/**
 * The tab icon and the sign-in lockup share this resolution order
 * (CAPTURE_JOBS.md section 5): the tenant's runtime branding logo when the
 * partner manifest has loaded, otherwise the partner build's favicon, then
 * the partner build's logo. The app's own letter icon shows only when no
 * favicon and no logo is configured anywhere.
 */
export function resolveBrandLogoUrl(manifest?: Pick<PartnerCaptureManifest, 'theme'> | null): string {
  return manifest?.theme.logoUrl || buildTimeLogoMarkUrl() || buildTimeLogoUrl() || DEFAULT_LOGO_MARK_URL;
}

/**
 * The home-screen icon an iPhone saves with "Add to Home Screen". iOS
 * ignores SVG touch icons, so the partner's raster logo comes before its
 * favicon, which is usually an SVG.
 */
export function resolveTouchIconUrl(manifest?: Pick<PartnerCaptureManifest, 'theme'> | null): string {
  const raster = (url?: string) => (url && !/\.svg(\?|$)/i.test(url) ? url : undefined);
  return (
    raster(manifest?.theme.logoUrl) ||
    raster(buildTimeLogoUrl()) ||
    raster(buildTimeLogoMarkUrl()) ||
    resolveBrandLogoUrl(manifest)
  );
}

function setLink(rel: string, href: string): void {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
}

/**
 * Sets the browser tab icon (`link[rel="icon"]`) from the brand logo
 * mark. `index.html` loads this module directly as a startup hook, which
 * applies the build-time default before React ever mounts; call it again
 * with the partner manifest once it has loaded (`usePartnerManifest`) so
 * a signed-in tenant's own mark takes over.
 */
export function applyFavicon(manifest?: Pick<PartnerCaptureManifest, 'theme'> | null): void {
  setLink('icon', resolveBrandLogoUrl(manifest));
  setLink('apple-touch-icon', resolveTouchIconUrl(manifest));
}

// Startup hook: index.html loads this module directly (not through
// main.tsx), so the build-time default favicon is set as soon as the
// document parses, with no dependency on the manifest fetch or React.
if (typeof document !== 'undefined') {
  applyFavicon();
}
